<template>
  <v-row
    id="checkout-container"
    class="shopping-cart"
  >
    <v-col>
      <div class="stacked-widget-header">
        <h1 class="d-none d-sm-block">
          {{ $t('checkout-cart-title') }}
        </h1>
        <h2 class="d-flex align-center justify-space-between">
          <span>
            Total Items (<span>{{ cartItems.length }}</span>)
          </span>
          <tc-v-btn
            id="continue-shopping-btn"
            text
            color="primary"
            class="text-capitalize px-0"
            @click="continueShopping()"
          >
            {{ $t('checkout-continue') }}
          </tc-v-btn>
        </h2>
      </div>
      <div
        v-if="cartItems.length === 0"
        class="cart-empty-container"
      >
        <p class="cart-empty">
          {{ $t('global-cart-no-items') }}
        </p>
      </div>
      <div
        v-for="(cartItem, index) in cartItems"
        id="items-review"
        :key="index"
      >
        <item-control :cart-item="cartItem" />
        <div class="svp-hr" />
      </div>
      <div
        v-if="cartItems.length > 0"
        class="stacked-widget-footer"
      >
        <h1>
          Total:
          <span id="cart-total">
            {{ formattedCartTotal }}
          </span>
        </h1>
      </div>
      <div class="text-center">
        <tc-v-btn
          id="checkout-complete-btn"
          :class="{ disabled: cartItems.length === 0 }"
          :disabled="cartItems.length === 0"
          color="accent"
          block
          large
          tabindex="0"
          @click="checkOut"
          @keyup.enter.prevent="checkOut"
        >
          {{ $t('global-button-checkout') }}
        </tc-v-btn>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  GET_CART_ITEMS,
  GET_CURRENCY_CODE,
  GET_CART_TOTAL,
  ORDER_TRACKING_PROPERTIES
} from '../../store/get-types'
import ItemControl from './ItemControl'
import { CART_VIEWED } from '@/plugins/segment/SegmentConstants'
import { CHECKOUT_ROUTE, CATALOG_ROUTE } from '@/router/routes/route-names'

export default {
  name: 'ItemLayout',
  components: {
    'item-control': ItemControl
  },
  data () {
    return {
      inputValue: 0
    }
  },
  computed: {
    ...mapGetters({
      cartItems: GET_CART_ITEMS,
      currencyCode: GET_CURRENCY_CODE,
      cartTotal: GET_CART_TOTAL,
      orderTrackingProperties: ORDER_TRACKING_PROPERTIES
    }),
    formattedCartTotal () {
      return this.$money(this.cartTotal, this.currencyCode)
    }
  },
  mounted () {
    this.$segment.track(CART_VIEWED, this.orderTrackingProperties)
  },
  methods: {
    continueShopping () {
      this.$router.push({ name: CATALOG_ROUTE })
    },
    checkOut () {
      this.$router.push({ name: CHECKOUT_ROUTE })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/css/variables';

.cart-empty-container {
  padding: 4vw;
  clear: both;

  .cart-empty {
    margin: 0;
    width: 100%;
    text-align: center;
    font-size: 20px;
  }
}

.shopping-cart {
  padding-top: 72px;
}

.stacked-widget-header {
  font-family: 'Gotham-Book', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: $tangoTextPrimary;

  h1 {
    color: $tangoBlueGrayDk;
    font-size: 18px;
    line-height: 22px;
    margin: 0 0 10px 0;
    padding-bottom: 6px;
    border-bottom: 2px $tangoBlueGrayDk solid;
    @media screen and (max-width: $breakpoint-md - 1) {
      margin: 0 -15px 22px -15px;
      padding: 16px 15px;
      border-top: 1px solid $tangoBlueGrayDk;
      border-bottom: 1px solid $tangoBlueGrayDk;
      font-size: 16px;
    }
  }

  h2 {
    color: $tangoBlueGrayDk;
    font-size: 15px;
    line-height: 18px;
    margin: 0;
    padding: 3px 0;
    border-bottom: 1px $tangoBlueGrayDk solid;
  }
}

#items-review {
  .cart-item {
    margin: 30px 0;
    min-height: 92px;
    position: relative;
  }

  .row {
    padding: 0 15px;
  }

  .name {
    font-size: $font-size-lg;
    color: $tangoTextPrimary;
  }

  .formattedAmount {
    font-size: 18px;
    color: $tangoTextPrimary;
    margin: 0;
  }

  .qty {
    font-size: $font-size-md;
    color: $tangoTextPrimary;
  }
}

.edit-value {
  line-height: 18px;

  .chosen-container,
  .chosen-single {
    border: none;
    padding-left: 10px;
  }

  .chosen-drop {
    width: calc(100% + 2px);
  }

  .chosen-results,
  li {
    font-size: $font-size-md;
    color: $tangoTextPrimary;
    padding-top: 8px;
    padding-right: 25px;
  }
}

@media screen and (max-width: $breakpoint-md - 1) {
  .shopping-cart {
    #continue-shopping-btn {
      margin-top: 0;
    }
  }

  #items-review {
    max-height: 236px;
    min-height: 80px;
    font-weight: bold;
    color: $tangoTextPrimary;

    .name {
      font-size: $font-size-lg;
    }

    .formattedAmount {
      font-size: $font-size-md;
      color: $tangoTextPrimary;
      margin: 6px 0 0 0;
    }

    .qty {
      font-size: $font-size-sm;
      color: $tangoTextPrimary;
    }
  }
}

@media screen and (max-width: $breakpoint-md - 1) {
  #items-review {
    border-radius: 0;
    background-color: #fff;
    color: $tangoBlueGrayDk;
    min-width: 50px;
    min-height: 40px;
    margin: 0;

    .controls {
      position: absolute;
      right: 0;
      padding: 0;
      top: 2px;

      div {
        cursor: pointer;
        padding: 10px 4px;
        border-radius: 0;
        border: 1px solid $tangoBlueGrayLt;
        display: inline-block;
        min-width: 80px;
        text-align: center;
      }
    }
  }
}

@media screen and (min-width: $breakpoint-md) {
  .edit-value {
    position: relative;
    z-index: 1;
  }
}

@media screen and (max-width: $breakpoint-sm - 1) {
  .shopping-cart {
    padding-top: 25px;
  }

  #items-review .cart-item {
    margin: 15px 0;
  }

  .controls {
    max-width: 85px;
  }
}
</style>
